import React from 'react';

import capitalize from 'lodash/capitalize';
import PropTypes from 'prop-types';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import iconShape from '../../../types/icon';

const MAX_OVERLAPPING_ICONS = 3;

const pluralize = (word) => {
  if (word.endsWith('y')) return `${word.slice(0, -1)}ies`;
  if (word.endsWith('s')) return `${word}es`;

  return `${word}s`;
};

const OverlappingIcons = ({ options }) => (
  <span className="flex items-center">
    {options.slice(0, MAX_OVERLAPPING_ICONS).map((option, idx) => (
      <span className={idx > 0 ? '-ml-1' : ''} key={option.value}>
        {option.icon}
      </span>
    ))}
  </span>
);

const optionShape = PropTypes.shape({
  icon: PropTypes.element.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
});

OverlappingIcons.propTypes = {
  options: PropTypes.arrayOf(optionShape).isRequired,
};

const MultiSelectField = ({ control, entity, icon, name, options, disabled = false }) => {
  const entityLabel = capitalize(entity);
  const entityPlural = pluralize(entity);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const selectedValues = field.value;
        const selectedCount = selectedValues.length;

        const selectedOptions = options.filter((option) =>
          selectedValues.includes(option.value)
        );

        const buttonStyles = cn(
          'max-w-28 gap-1.5 text-xs data-[state=open]:bg-accent',
          'data-[state=open]:text-accent-foreground',
          selectedCount > 0 ? 'text-accent-foreground' : 'text-muted-foreground'
        );

        const handleSelect = (value) => {
          const newValues = selectedValues.includes(value)
            ? selectedValues.filter((v) => v !== value)
            : [...selectedValues, value];

          field.onChange(newValues);
        };

        return (
          <FormItem>
            <FormLabel className="sr-only">{entityLabel}</FormLabel>

            <Popover>
              <FormControl>
                <PopoverTrigger asChild disabled={disabled}>
                  <Button className={buttonStyles} variant="outline">
                    {selectedCount > 0 ? (
                      <>
                        <OverlappingIcons options={selectedOptions} />
                        {selectedCount} {selectedCount === 1 ? entityLabel : entityPlural}
                      </>
                    ) : (
                      <>
                        {icon} {entityLabel}
                      </>
                    )}
                  </Button>
                </PopoverTrigger>
              </FormControl>

              <PopoverContent align="start" className="w-52 p-0">
                <Command className="[&_[cmdk-input-wrapper]]:h-8 [&_[cmdk-item]]:py-1">
                  <CommandInput placeholder={`Search ${entityPlural}...`} />

                  <CommandList>
                    <CommandEmpty>{`No ${entityPlural} found.`}</CommandEmpty>

                    <CommandGroup>
                      {options.map((option) => {
                        const isSelected = selectedValues.includes(option.value);

                        return (
                          <CommandItem
                            key={option.value}
                            onSelect={() => handleSelect(option.value)}
                            value={option.label}
                          >
                            <Checkbox checked={isSelected} />
                            {option.icon}
                            <span className="truncate">{option.label}</span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

MultiSelectField.propTypes = {
  control: PropTypes.shape({}).isRequired,
  entity: PropTypes.string.isRequired,
  icon: iconShape.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(optionShape).isRequired,
  disabled: PropTypes.bool,
};

export default MultiSelectField;
